import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, UserPlus, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManualOnboarding = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Location data
  const [locationData, setLocationData] = useState({
    name: '',
    city: '',
    state: '',
    address: '',
    phone: '',
    timezone: 'America/New_York'
  });
  const [createdLocationId, setCreatedLocationId] = useState(null);

  // User data
  const [userData, setUserData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'admin'
  });

  // Subscription data
  const [subscriptionPlan, setSubscriptionPlan] = useState('starter');

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/onboard/location`,
        locationData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      setCreatedLocationId(response.data.location_id);
      setSuccess(`Location "${locationData.name}" created successfully!`);
      setStep(2);
    } catch (err) {
      console.error('Location creation error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to create location';
      setError(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/onboard/user`,
        {
          ...userData,
          location_id: createdLocationId,
          subscription_plan: subscriptionPlan
        },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      setSuccess(`Admin account created for ${userData.email}! They can now log in.`);
      setStep(3);
    } catch (err) {
      console.error('User creation error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to create user';
      setError(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setLocationData({
      name: '',
      city: '',
      state: '',
      address: '',
      phone: '',
      timezone: 'America/New_York'
    });
    setUserData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'admin'
    });
    setSubscriptionPlan('starter');
    setCreatedLocationId(null);
    setError('');
    setSuccess('');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Manual Location Onboarding</h1>
        <p className="text-gray-600 mt-2">Create new locations and admin accounts for testing</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-8">
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
            {step > 1 ? <CheckCircle className="w-6 h-6" /> : '1'}
          </div>
          <div className={`w-24 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`} />
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
            {step > 2 ? <CheckCircle className="w-6 h-6" /> : '2'}
          </div>
          <div className={`w-24 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`} />
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 3 ? 'bg-green-600 text-white' : 'bg-gray-300'}`}>
            {step > 3 ? <CheckCircle className="w-6 h-6" /> : '3'}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Step 1: Create Location */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Step 1: Create Location
            </CardTitle>
            <CardDescription>Enter the new location details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLocationSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Location Name *</Label>
                <Input
                  id="name"
                  value={locationData.name}
                  onChange={(e) => setLocationData({ ...locationData, name: e.target.value })}
                  placeholder="K9 Resorts of Garden City"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={locationData.city}
                    onChange={(e) => setLocationData({ ...locationData, city: e.target.value })}
                    placeholder="Garden City"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={locationData.state}
                    onChange={(e) => setLocationData({ ...locationData, state: e.target.value })}
                    placeholder="NY"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={locationData.address}
                  onChange={(e) => setLocationData({ ...locationData, address: e.target.value })}
                  placeholder="123 Main St"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={locationData.phone}
                  onChange={(e) => setLocationData({ ...locationData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={locationData.timezone} onValueChange={(value) => setLocationData({ ...locationData, timezone: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Creating...' : 'Create Location'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Create Admin User */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Step 2: Create Admin Account
            </CardTitle>
            <CardDescription>Create an admin user for {locationData.name}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={userData.first_name}
                    onChange={(e) => setUserData({ ...userData, first_name: e.target.value })}
                    placeholder="Glenn"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={userData.last_name}
                    onChange={(e) => setUserData({ ...userData, last_name: e.target.value })}
                    placeholder="Smith"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={userData.email}
                  onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                  placeholder="glenn@k9resorts.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={userData.password}
                  onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                  placeholder="Enter password"
                  required
                />
              </div>

              <div>
                <Label htmlFor="plan">Subscription Plan</Label>
                <Select value={subscriptionPlan} onValueChange={setSubscriptionPlan}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter ($199/mo) - 3 users, Basic features</SelectItem>
                    <SelectItem value="professional">Professional ($399/mo) - Unlimited users, AI Insights</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500 mt-1">14-day free trial included</p>
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Creating...' : 'Create Admin Account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Success & Setup Instructions */}
      {step === 3 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-6 h-6" />
                Onboarding Complete!
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 mb-2">✅ Location Created:</h3>
                <p className="text-green-800 font-medium">{locationData.name}</p>
                <p className="text-sm text-green-700">{locationData.city}, {locationData.state}</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">✅ Admin Account Created:</h3>
                <p className="text-blue-800 font-medium">{userData.email}</p>
                <p className="text-sm text-blue-600 mt-1">Password: <code className="bg-blue-100 px-2 py-1 rounded">{userData.password}</code></p>
                <p className="text-xs text-blue-500 mt-2">💡 Share these credentials with the location admin</p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-900 mb-2">✅ Subscription Active:</h3>
                <p className="text-purple-800 font-medium capitalize">{subscriptionPlan} Plan</p>
                <p className="text-sm text-purple-600">🎉 14-day free trial started (no credit card required yet)</p>
              </div>
            </CardContent>
          </Card>

          {/* Setup Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">📋 Next Steps: Setup Instructions</CardTitle>
              <CardDescription>Share these steps with the location admin to complete their setup</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 1: Login */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
                  Login to Your Account
                </h4>
                <div className="ml-8 text-sm text-gray-700 space-y-1">
                  <p>• Visit: <a href={window.location.origin} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">{window.location.origin}</a></p>
                  <p>• Email: <code className="bg-gray-100 px-2 py-1 rounded">{userData.email}</code></p>
                  <p>• Password: <code className="bg-gray-100 px-2 py-1 rounded">{userData.password}</code></p>
                </div>
              </div>

              {/* Step 2: Setup Gingr Integration */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
                  Connect Gingr POS (Required)
                </h4>
                <div className="ml-8 text-sm text-gray-700 space-y-2">
                  <p className="font-medium">To sync leads with POS data:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Go to <strong>Settings</strong> → <strong>Integrations</strong></li>
                    <li>Find the <strong>Gingr POS</strong> section</li>
                    <li>Enter your Gingr API credentials:
                      <ul className="ml-4 mt-1 list-disc">
                        <li><strong>App URL:</strong> Your Gingr subdomain (e.g., yourcompany.gingrapp.com)</li>
                        <li><strong>API Key:</strong> Get from Gingr Settings → API Access</li>
                      </ul>
                    </li>
                    <li>Click <strong>Save & Test Connection</strong></li>
                  </ol>
                  <p className="text-xs text-gray-500 mt-2">💡 Contact Gingr support if you need API access enabled</p>
                </div>
              </div>

              {/* Step 3: Setup Gmail Integration */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
                  Connect Gmail (Optional but Recommended)
                </h4>
                <div className="ml-8 text-sm text-gray-700 space-y-2">
                  <p className="font-medium">To auto-capture leads from email forms:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Go to <strong>Settings</strong> → <strong>Integrations</strong></li>
                    <li>Find the <strong>Gmail IMAP</strong> section</li>
                    <li>Generate a Gmail App Password:
                      <ul className="ml-4 mt-1 list-disc">
                        <li>Go to <a href="https://myaccount.google.com/apppasswords" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">Google App Passwords</a></li>
                        <li>Create app password for "Mail"</li>
                        <li>Copy the 16-character password</li>
                      </ul>
                    </li>
                    <li>Enter your Gmail and App Password</li>
                    <li>Click <strong>Connect Gmail</strong></li>
                  </ol>
                </div>
              </div>

              {/* Step 4: Invite Staff */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">4</span>
                  Invite Your Team
                </h4>
                <div className="ml-8 text-sm text-gray-700 space-y-1">
                  <p>• Go to <strong>Users</strong> → <strong>Invite User</strong></p>
                  <p>• Enter their email and assign role (Manager/Agent)</p>
                  <p>• They'll receive an invitation to create their account</p>
                  {subscriptionPlan === 'starter' && (
                    <p className="text-amber-600 text-xs mt-2">⚠️ Starter plan allows up to 3 users</p>
                  )}
                </div>
              </div>

              {/* What You Get */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">🎯 What You'll Get:</h4>
                <ul className="text-sm text-blue-800 space-y-1 ml-4">
                  <li>✅ Automatic lead capture from emails</li>
                  <li>✅ POS matching for converted customers</li>
                  <li>✅ Lead tracking & follow-up reminders</li>
                  {subscriptionPlan === 'professional' && (
                    <>
                      <li>✅ AI-powered business insights</li>
                      <li>✅ Advanced analytics & reports</li>
                    </>
                  )}
                  <li>✅ Real-time dashboard & metrics</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button onClick={resetForm} variant="outline" className="flex-1">
              Onboard Another Location
            </Button>
            <Button onClick={() => window.location.href = '/'} className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600">
              Go to Dashboard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualOnboarding;
